import { useState, useCallback, useEffect, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useUser } from '../contexts/UserContext'
import { useChatStore } from '../stores/chatStore'
import type { Database } from '../types/supabase'

// Type definitions
export type ChatTab = Database['public']['Tables']['chat_tabs']['Row']

export interface TabManager {
    tabs: ChatTab[]
    activeTabId: string
    loading: boolean
    openTab: (agentId: string | null, agentName: string) => Promise<ChatTab | null>
    closeTab: (tabId: string) => Promise<void>
    switchTab: (tabId: string) => Promise<void>
    reorderTabs: (tabId: string, newPosition: number) => Promise<void>
    reload: () => Promise<void>
}

/**
 * Hook for managing chat tabs with database persistence
 * @param pageContext - Context to isolate tabs by page (e.g., '/agentes/ana')
 */
export function useTabManager(pageContext: string = 'global'): TabManager {
    const { user } = useUser()
    const [tabs, setTabs] = useState<ChatTab[]>([])
    const [activeTabId, setActiveTabId] = useState<string>('')
    const [loading, setLoading] = useState(true)

    console.log('[useTabManager] Initialized with pageContext:', pageContext)

    // Load tabs from database
    const loadTabs = useCallback(async () => {
        if (!user) {
            console.log('[useTabManager] loadTabs - No user')
            setLoading(false)
            return
        }

        console.log('[useTabManager] loadTabs - START', { userId: user.id, pageContext })
        setLoading(true)

        try {
            const { data, error } = await supabase
                .from('chat_tabs')
                .select('*')
                .eq('page_context', pageContext)
                .order('position')

            if (error) throw error

            console.log('[useTabManager] loadTabs - SUCCESS', {
                count: data?.length,
                data: data,
                pageContext,
                userId: user.id
            })
            setTabs(data || [])

            const activeTab = data?.find(t => t.is_active)
            setActiveTabId(activeTab?.id || '')
        } catch (error) {
            console.error('[useTabManager] loadTabs - ERROR:', error)
            setTabs([])
            setActiveTabId('')
        } finally {
            setLoading(false)
        }
    }, [user, pageContext])

    const openTab = useCallback(async (agentId: string | null, agentName: string): Promise<ChatTab | null> => {
        console.log('[useTabManager] openTab - START', { agentId, agentName, pageContext })

        // Check if tab already exists for this agent
        const existing = tabs.find(t => t.agent_id === agentId)
        if (existing) {
            console.log('[useTabManager] openTab - Tab exists, switching to it', existing.id)
            // Call RPC directly to avoid circular dependency
            await supabase.rpc('switch_active_tab', { p_tab_id: existing.id })
            await loadTabs()
            return existing
        }

        try {
            const { data, error } = await supabase.rpc('create_chat_tab', {
                p_agent_id: agentId,
                p_agent_name: agentName,
                p_page_context: pageContext,
                // Do not pin System Copilot by default (was agentId === null)
                p_is_pinned: false
            })

            if (error) throw error

            console.log('[useTabManager] openTab - SUCCESS', data)
            await loadTabs() // Reload to get updated state
            return data
        } catch (error) {
            console.error('[useTabManager] openTab - ERROR:', error)
            return null
        }
    }, [tabs, pageContext, loadTabs])

    const closeTab = useCallback(async (tabId: string): Promise<void> => {
        console.log('[useTabManager] closeTab - START', { tabId })

        try {
            const { error } = await supabase.rpc('close_chat_tab', {
                p_tab_id: tabId
            })

            if (error) throw error

            console.log('[useTabManager] closeTab - SUCCESS')
            await loadTabs() // Reload to get updated state
        } catch (error) {
            console.error('[useTabManager] closeTab - ERROR:', error)
        }
    }, [loadTabs])

    const switchTab = useCallback(async (tabId: string): Promise<void> => {
        console.log('[useTabManager] switchTab - START', { tabId })

        try {
            const { error } = await supabase.rpc('switch_active_tab', {
                p_tab_id: tabId
            })

            if (error) throw error

            console.log('[useTabManager] switchTab - SUCCESS')
            setActiveTabId(tabId)
        } catch (error) {
            console.error('[useTabManager] switchTab - ERROR:', error)
        }
    }, [])

    const reorderTabs = useCallback(async (tabId: string, newPosition: number): Promise<void> => {
        console.log('[useTabManager] reorderTabs - START', { tabId, newPosition })

        try {
            const { error } = await supabase.rpc('reorder_tabs', {
                p_tab_id: tabId,
                p_new_position: newPosition
            })

            if (error) throw error

            console.log('[useTabManager] reorderTabs - SUCCESS')
            await loadTabs() // Reload to get updated state
        } catch (error) {
            console.error('[useTabManager] reorderTabs - ERROR:', error)
        }
    }, [loadTabs])

    // Load tabs on mount
    useEffect(() => {
        loadTabs()
    }, [loadTabs])

    // Real-time subscriptions
    useEffect(() => {
        if (!user) return

        console.log('[useTabManager] Setting up real-time subscriptions')

        // Subscribe to tab changes
        const tabsChannel = supabase
            .channel('chat_tabs_changes')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'chat_tabs',
                    filter: `user_id=eq.${user.id}`
                },
                (payload) => {
                    console.log('[useTabManager] Tab change detected:', payload)
                    loadTabs()
                }
            )
            .subscribe()

        // Subscribe to message changes (for unread counts)
        const messagesChannel = supabase
            .channel('messages_changes')
            .on(
                'postgres_changes',
                {
                    event: 'INSERT',
                    schema: 'public',
                    table: 'messages'
                },
                (payload) => {
                    console.log('[useTabManager] New message detected:', payload)
                    loadTabs() // Reload to update unread counts
                }
            )
            .subscribe()

        return () => {
            console.log('[useTabManager] Cleaning up subscriptions')
            supabase.removeChannel(tabsChannel)
            supabase.removeChannel(messagesChannel)
        }
    }, [user, loadTabs])

    // Phase 3: Pre-initialize Zustand store for all open tabs
    // This ensures instant cache hydration when switching tabs
    useEffect(() => {
        const initAgent = useChatStore.getState().initAgent

        tabs.forEach(tab => {
            if (tab.agent_id && tab.name) {
                // Initialize agent in store if not already present
                // This is idempotent - initAgent checks if already initialized
                initAgent(tab.agent_id, tab.name)
            }
        })
    }, [tabs])

    return useMemo(() => ({
        tabs,
        activeTabId,
        loading,
        openTab,
        closeTab,
        switchTab,
        reorderTabs,
        reload: loadTabs,
    }), [tabs, activeTabId, loading, openTab, closeTab, switchTab, reorderTabs, loadTabs])
}
